import React, { useState, useEffect } from 'react';
import { api } from '../api';
import DokumenteSection from '../components/DokumenteSection';
import KontaktForm from '../components/KontaktForm';
import PersonPicker from '../components/PersonPicker';

// ─── AnsprechpartnerForm Modal ────────────────────────────────────────────────

function AnsprechpartnerForm({ initial, kontaktName, onSave, onClose }) {
  const [form, setForm] = useState(initial ? { ...initial } : {
    name: '', telefon: '', mobil: '', email: '', funktion: '', notizen: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name ist erforderlich.'); return; }
    setSaving(true);
    try { await onSave(form); }
    catch (err) { setError(err.message); setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{initial ? 'Ansprechpartner bearbeiten' : `Ansprechpartner für: ${kontaktName}`}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group form-full">
                <label className="form-label">Name *</label>
                <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Funktion / Position</label>
                <input className="form-input" value={form.funktion || ''} onChange={e => set('funktion', e.target.value)} placeholder="z.B. Serviceberater, Buchhalter" />
              </div>
              <div className="form-group">
                <label className="form-label">Telefon</label>
                <input className="form-input" type="tel" value={form.telefon || ''} onChange={e => set('telefon', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Mobil</label>
                <input className="form-input" type="tel" value={form.mobil || ''} onChange={e => set('mobil', e.target.value)} />
              </div>
              <div className="form-group form-full">
                <label className="form-label">E-Mail</label>
                <input className="form-input" type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Notizen</label>
                <textarea className="form-textarea" rows={2} value={form.notizen || ''} onChange={e => set('notizen', e.target.value)} />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Speichern…' : 'Speichern'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

const TYP_BADGE_CLASS = {
  'Kunde': 'badge-blue',
  'Lieferant': 'badge-success',
  'Werkstatt': 'badge-success',
  'Versicherer': 'badge-warning',
  'Leasinggeber': 'badge-warning',
  'TÜV': 'badge-gray',
  'Pannendienst': 'badge-danger',
  'Reifenservice': 'badge-success',
  'Kontakt': 'badge-gray',
  'Sonstiges': 'badge-gray',
};

// ─── KontaktRow (expandable) ──────────────────────────────────────────────────

function KontaktRow({ kontakt, onEdit, onDelete, onAddAP, onEditAP, onDeleteAP }) {
  const [expanded, setExpanded] = useState(false);

  const adresse = [kontakt.strasse, [kontakt.plz, kontakt.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const typen = (kontakt.typ || '').split(',').map(t => t.trim()).filter(Boolean);

  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(e => !e)}>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--gray-400)', fontSize: 11, width: 12 }}>{expanded ? '▼' : '▶'}</span>
            <strong>{kontakt.name}</strong>
            {typen.filter(t => t !== 'Kunde').map(t => (
              <span key={t} className={`badge ${TYP_BADGE_CLASS[t] || 'badge-gray'}`} style={{ fontSize: 10 }}>{t}</span>
            ))}
          </div>
        </td>
        <td style={{ fontSize: 12, color: 'var(--gray-600)' }}>{adresse || '–'}</td>
        <td>
          {kontakt.telefon
            ? <a href={`tel:${kontakt.telefon}`} onClick={e => e.stopPropagation()} style={{ color: 'var(--primary)', fontWeight: 500 }}>{kontakt.telefon}</a>
            : '–'}
        </td>
        <td>
          {kontakt.email
            ? <a href={`mailto:${kontakt.email}`} onClick={e => e.stopPropagation()} style={{ color: 'var(--primary)' }}>{kontakt.email}</a>
            : '–'}
        </td>
        <td>
          <span className="badge badge-gray">{(kontakt.ansprechpartner || []).length}</span>
        </td>
        <td onClick={e => e.stopPropagation()}>
          <div className="td-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => onEdit(kontakt)}>✏️</button>
            <button className="btn btn-danger btn-sm" onClick={() => onDelete(kontakt.id)}>🗑️</button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} style={{ padding: 0, background: 'var(--gray-50)' }}>
            <div style={{ padding: '8px 16px 8px 48px' }}>
              {/* Ansprechpartner */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Ansprechpartner</div>
                  <button className="btn btn-primary btn-sm" onClick={() => onAddAP(kontakt)}>+ Ansprechpartner hinzufügen</button>
                </div>
                {(kontakt.ansprechpartner || []).length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--gray-400)', padding: '4px 0' }}>
                    Noch keine Ansprechpartner eingetragen.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {['Name', 'Funktion', 'Telefon', 'Mobil', 'E-Mail', ''].map(h => (
                          <th key={h} style={{ fontSize: 11, color: 'var(--gray-500)', padding: '4px 8px', textAlign: 'left', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {kontakt.ansprechpartner.map(ap => (
                        <tr key={ap.id}>
                          <td style={{ padding: '6px 8px', fontSize: 13, fontWeight: 500 }}>{ap.name}</td>
                          <td style={{ padding: '6px 8px', fontSize: 12, color: 'var(--gray-500)' }}>{ap.funktion || '–'}</td>
                          <td style={{ padding: '6px 8px', fontSize: 12 }}>
                            {ap.telefon ? <a href={`tel:${ap.telefon}`} style={{ color: 'var(--primary)' }}>{ap.telefon}</a> : '–'}
                          </td>
                          <td style={{ padding: '6px 8px', fontSize: 12 }}>
                            {ap.mobil ? <a href={`tel:${ap.mobil}`} style={{ color: 'var(--primary)' }}>{ap.mobil}</a> : '–'}
                          </td>
                          <td style={{ padding: '6px 8px', fontSize: 12 }}>
                            {ap.email ? <a href={`mailto:${ap.email}`} style={{ color: 'var(--primary)' }}>{ap.email}</a> : '–'}
                          </td>
                          <td style={{ padding: '6px 8px' }}>
                            <div className="td-actions">
                              <button className="btn btn-secondary btn-sm" onClick={() => onEditAP(ap, kontakt)}>✏️</button>
                              <button className="btn btn-danger btn-sm" onClick={() => onDeleteAP(ap.id)}>🗑️</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Dokumente */}
              <DokumenteSection kontaktId={kontakt.id} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

const TYP_FILTER_OPTIONS = ['Alle', 'Kunde', 'Lieferant', 'Werkstatt', 'Versicherer', 'Sonstiges'];

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function KontakteListe({ navigate }) {
  const [kontakte, setKontakte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typFilter, setTypFilter] = useState('Alle');
  const [error, setError] = useState('');

  // Kontakt-Modals
  const [showKontaktForm, setShowKontaktForm] = useState(false);
  const [editKontakt, setEditKontakt] = useState(null);
  const [deleteKontaktId, setDeleteKontaktId] = useState(null);

  // Ansprechpartner-Modals
  const [showAPPicker, setShowAPPicker] = useState(false);  // PersonPicker zum Hinzufügen
  const [showAPForm, setShowAPForm] = useState(false);      // Formular zum Bearbeiten
  const [editAP, setEditAP] = useState(null);
  const [apKontakt, setApKontakt] = useState(null);
  const [deleteAPId, setDeleteAPId] = useState(null);

  const load = () => {
    setLoading(true);
    api.getKontaktbuch().then(setKontakte).catch(e => setError(e.message)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Filter
  const filtered = kontakte.filter(k => {
    if (typFilter !== 'Alle') {
      const typen = (k.typ || '').split(',').map(t => t.trim());
      if (!typen.includes(typFilter)) return false;
    }
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [k.name, k.ort, k.telefon, k.email, ...(k.ansprechpartner||[]).map(ap => ap.name)]
      .some(v => (v||'').toLowerCase().includes(q));
  });

  // Kontakt speichern (API-Calls passieren im KontaktForm selbst)
  const handleKontaktSave = () => {
    setShowKontaktForm(false); setEditKontakt(null); load();
  };

  // Kontakt löschen
  const handleKontaktDelete = async () => {
    try {
      await api.deleteKontakt(deleteKontaktId);
      setDeleteKontaktId(null); load();
    } catch (e) {
      alert(e.message);
      setDeleteKontaktId(null);
    }
  };

  // Person aus PersonPicker ausgewählt → verknüpfen
  const handleAPPickerSelect = async (person) => {
    setShowAPPicker(false);
    try {
      await api.createAnsprechpartner(apKontakt.id, { ansprechpartner_id: person.id });
      setApKontakt(null); load();
    } catch (e) { alert(e.message); }
  };

  // Ansprechpartner bearbeiten speichern
  const handleAPSave = async (data) => {
    await api.updateAnsprechpartner(editAP.id, data);
    setShowAPForm(false); setEditAP(null); setApKontakt(null); load();
  };

  // Ansprechpartner von Kontakt trennen (Person bleibt in DB)
  const handleAPDelete = async () => {
    const kontakt = kontakte.find(k => (k.ansprechpartner || []).some(ap => ap.id === deleteAPId));
    if (kontakt) {
      await api.unlinkAnsprechpartner(kontakt.id, deleteAPId).catch(e => alert(e.message));
    } else {
      await api.deleteAnsprechpartner(deleteAPId).catch(e => alert(e.message));
    }
    setDeleteAPId(null); load();
  };

  const totalAP = kontakte.reduce((n, k) => n + (k.ansprechpartner?.length || 0), 0);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Kontaktbuch</div>
          <div className="page-subtitle">{kontakte.length} Kontakte · {totalAP} Ansprechpartner</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditKontakt(null); setShowKontaktForm(true); }}>
          + Kontakt hinzufügen
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Type filter pills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {TYP_FILTER_OPTIONS.map(t => (
          <button
            key={t}
            onClick={() => setTypFilter(t)}
            style={{
              padding: '4px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500,
              border: `1px solid ${typFilter === t ? 'var(--primary)' : 'var(--gray-200)'}`,
              background: typFilter === t ? 'var(--primary)' : '#fff',
              color: typFilter === t ? '#fff' : 'var(--gray-600)',
              cursor: 'pointer', transition: 'all .15s'
            }}
          >{t}</button>
        ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        <input
          className="form-input"
          placeholder="Suchen nach Name, Ort, Telefon, E-Mail, Ansprechpartner…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 400 }}
        />
      </div>

      {loading ? (
        <div className="spinner">Lade Kontakte…</div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="card-body empty-state">
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-title">{search ? 'Keine Treffer' : 'Noch keine Kontakte'}</div>
            <div className="empty-state-desc">{search ? 'Suchbegriff anpassen.' : 'Ersten Kontakt hinzufügen.'}</div>
          </div>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name / Firma · Typ</th>
                <th>Adresse</th>
                <th>Telefon</th>
                <th>E-Mail</th>
                <th>AP</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(k => (
                <KontaktRow
                  key={k.id}
                  kontakt={k}
                  onEdit={k => { setEditKontakt(k); setShowKontaktForm(true); }}
                  onDelete={setDeleteKontaktId}
                  onAddAP={k => { setApKontakt(k); setShowAPPicker(true); }}
                  onEditAP={(ap, k) => { setEditAP(ap); setApKontakt(k); setShowAPForm(true); }}
                  onDeleteAP={setDeleteAPId}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showKontaktForm && (
        <KontaktForm
          initial={editKontakt}
          onSave={handleKontaktSave}
          onClose={() => { setShowKontaktForm(false); setEditKontakt(null); }}
        />
      )}

      {showAPPicker && apKontakt && (
        <PersonPicker
          onSelect={handleAPPickerSelect}
          onClose={() => { setShowAPPicker(false); setApKontakt(null); }}
          excludeIds={(apKontakt.ansprechpartner || []).map(ap => ap.id)}
          kontaktId={apKontakt.id}
        />
      )}

      {showAPForm && apKontakt && editAP && (
        <AnsprechpartnerForm
          initial={editAP}
          kontaktName={apKontakt.name}
          onSave={handleAPSave}
          onClose={() => { setShowAPForm(false); setEditAP(null); setApKontakt(null); }}
        />
      )}

      {deleteKontaktId && (
        <div className="modal-overlay" onClick={() => setDeleteKontaktId(null)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Kontakt löschen</span>
              <button className="modal-close" onClick={() => setDeleteKontaktId(null)}>✕</button>
            </div>
            <div className="modal-body confirm-dialog">
              <p>Kontakt wirklich löschen? Alle zugehörigen Ansprechpartner werden ebenfalls gelöscht.<br />
                <strong>Nicht möglich, wenn Ansprechpartner noch Fahrzeugen zugewiesen sind.</strong>
              </p>
              <div className="confirm-actions">
                <button className="btn btn-secondary" onClick={() => setDeleteKontaktId(null)}>Abbrechen</button>
                <button className="btn btn-danger" onClick={handleKontaktDelete}>Löschen</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteAPId && (
        <div className="modal-overlay" onClick={() => setDeleteAPId(null)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Ansprechpartner entfernen</span>
              <button className="modal-close" onClick={() => setDeleteAPId(null)}>✕</button>
            </div>
            <div className="modal-body confirm-dialog">
              <p>Person von diesem Kontakt trennen? Die Person bleibt in der zentralen Datenbank erhalten und kann anderen Kontakten oder Fahrzeugen zugeordnet bleiben.</p>
              <div className="confirm-actions">
                <button className="btn btn-secondary" onClick={() => setDeleteAPId(null)}>Abbrechen</button>
                <button className="btn btn-danger" onClick={handleAPDelete}>Trennen</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
